import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { useDispatch } from 'react-redux'
import { deleteMediaAction, getMediaDataAction, updateMediaAction, uploadMediaAction } from 'store/actions/media.action'
import { Loading } from 'components/index.js'
import TagsInput from './TagsInput/TagsInput'
import Image from 'next/image'
import Link from 'next/link'
import { audioExtensions, videoFileExts } from 'utils/constants'
import ReactAudioPlayer from 'react-audio-player'
import { cn } from '@/lib/utils'
import { Dialog, DialogContent, DialogClose } from '@components/ui/dialog'
import { Button } from '@components/ui/button'

const MediaTaggingModal = ({ files, setFiles, onClose }) => {
  const { t } = useTranslation('common')
  const dispatch = useDispatch()
  const [filesTags, setFilesTags] = useState([])
  const [loading, setLoading] = useState(false)
  const inputRef = useRef(null)

  useEffect(() => {
    setFilesTags(files.map((item) => item.tags))
  }, [files])

  const updateMediaTags = async () => {
    setLoading(true)
    await Promise.all(
      filesTags.map((fileTags, index) => {
        if (fileTags?.length) {
          dispatch(
            updateMediaAction(
              files[index].id,
              { tags: fileTags.includes('media') ? fileTags : [...fileTags, 'media'] },
              null
            )
          )
          fetchMedia()
        }
      })
    )
    setLoading(false)
  }

  const removeImage = async (key) => {
    dispatch(
      deleteMediaAction(files[key].id, () => {
        const updatedFiles = [...files]
        updatedFiles.splice(key, 1)
        setFilesTags(updatedFiles.map((item) => item.tags))
        fetchMedia()
        if (updatedFiles.length === 0) onClose()
      })
    )
  }

  const fetchMedia = () => {
    dispatch(getMediaDataAction())
  }

  const handleFile = async (files) => {
    let filesList = []
    const filesLength = files.length
    let errorsLength = 0
    setLoading(true)
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      const formData = new FormData()
      formData.append('media', file)
      dispatch(
        uploadMediaAction(
          formData,
          false,
          async (url, id) => {
            filesList.push({
              url,
              id,
              tags: [],
            })
            processingHandler(filesList, errorsLength, filesLength)
          },
          () => {
            errorsLength += 1
            processingHandler(filesList, errorsLength, filesLength)
          }
        )
      )
    }
    dispatch(getMediaDataAction())
  }

  const processingHandler = (filesList, errorsLength, filesLength) => {
    const percentage = (filesList.length + errorsLength) / filesLength
    if (percentage === 1 && filesList.length) {
      setFiles((prevFiles) => [...prevFiles, ...JSON.parse(JSON.stringify(filesList))])
      setLoading(false)
    }
    if (filesList.length + errorsLength === filesLength) {
      setLoading(false)
    }
  }

  const handleChange = function (e) {
    e.preventDefault()
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files)
    }
    e.target.value = ''
  }

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className={cn(
          'w-[92%] h-[80%] md:h-[96%] p-3 postupload-modal justify-start',
          'max-w-none bg-white rounded-lg shadow-lg'
        )}
      >
        {loading && <Loading />}
        <div className="mt-1 mb-3 text-lg font-bold text-center md:text-2xl md:mb-0">{t('common.uploaded-media')}</div>
        {filesTags.length > 0 && (
          <div className="flex flex-col p-4 pl-1 h-[80%] gap-8 overflow-y-auto media-upload-scrollbar pb-6">
            {files.map((item, key) => (
              <div className="w-full gap-6 flex px-3 py-3 flex-col rounded-[12px]" key={`uploaded-img-${key}`}>
                <div className="flex flex-col gap-5 top-img-container md:flex-row">
                  <div className={`w-full md:w-[40%] !relative ${files.length > 1 && 'w-full md:w-[35%]'}`}>
                    <div className="w-full h-full object-cover object-top rounded-[20px]">
                      {audioExtensions.includes(item.url.split('.').pop()) ? (
                        <ReactAudioPlayer
                          className="w-full h-full object-cover object-top rounded-[20px]"
                          src={item.url}
                          controls
                          width="100%"
                          height="100%"
                        />
                      ) : videoFileExts.includes(item.url.split('.').pop()) ? (
                        <video
                          className="w-full h-full object-cover object-top rounded-[20px]"
                          onMouseOver={(event) => event.target.play()}
                          onMouseOut={(event) => event.target.pause()}
                          src={item.url}
                        />
                      ) : (
                        <img
                          src={item.url}
                          className="w-full h-full object-cover object-top rounded-[20px]"
                          alt="tag media"
                        />
                      )}
                    </div>
                    <Image
                      src="/assets/common/close.svg"
                      className="absolute top-2 right-2 w-6 h-6 cursor-pointer"
                      onClick={() => removeImage(key)}
                      width={15}
                      height={15}
                      alt="close"
                    />
                  </div>
                  <div
                    className={`w-full md:w-[60%] flex flex-col gap-[10px] justify-start ${files.length > 1 && 'w-full md:w-[65%]'}`}
                  >
                    <h3 className="text-sm font-bold md:text-lg">{t('common.image-url')}:</h3>
                    <Link href={item.url} className="!text-blue-500 !underline imgurl-text" target="_blank">
                      {item.url}
                    </Link>
                    <div className={`flex flex-col gap-3 w-full ${key === files.length - 1 && 'mb-12 md:mb-0'}`}>
                      <h3 className="text-sm font-bold md:text-lg">{t('common.suggested-tags')}</h3>
                      <TagsInput
                        placeHolder="# Add Tag"
                        tagsList={filesTags[key]}
                        onChange={(tagsList) => {
                          const updatedFilesTags = [...filesTags]
                          updatedFilesTags[key] = tagsList
                          setFilesTags(updatedFilesTags)
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex absolute right-0 left-0 bottom-4 gap-2 justify-center px-4">
          <Button variant="outline" className="p-5 text-base" onClick={() => inputRef.current.click()}>
            Upload More
            <input
              ref={inputRef}
              type="file"
              id="input-file-upload"
              multiple={true}
              accept="image/*, video/*, audio/*"
              className="hidden"
              onChange={handleChange}
            />
          </Button>
          <Button
            className="p-5 text-base"
            onClick={async () => {
              await updateMediaTags()
              onClose()
            }}
          >
            {t('common.save')}
          </Button>
        </div>
        <DialogClose className="text-gray-500 hover:text-gray-700" />
      </DialogContent>
    </Dialog>
  )
}

export default MediaTaggingModal
